import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { Application } from "@core/Application";
import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { AlertDetail } from "@models/Alertas";
import { AlertsService } from "@services/AlertasService";
import { DateTriggerButton, HollowedButton, RefreshButton } from "@shared/designSystem/Button";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import {
    ColumnsContainer,
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer
} from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import SearchBar from "@shared/designSystem/SearchBar";
import { Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import LocalDateSelector from "@screens/Notificaciones/LocalDateSelector";

import KioskEvidencePhotos from "@screens/Listado/KioskEvidencePhotos";
import { AlertasListItem, isKioskEvidenceSpoofAlert, kioskEvidenceSpoofShiftLabel, typeToLabel, useAlertasListViewModel } from "./AlertasListViewModel";

interface AlertasListProps {
    orgID: string;
    timezone: string;
}

const columnWidths = {
    building: "minmax(0, 30fr)",
    shift: "minmax(0, 18fr)",
    schedule: "minmax(0, 16fr)",
    count: "minmax(0, 12fr)",
    date: "minmax(0, 18fr)",
};

const tableGridTemplateColumns = [
    columnWidths.building,
    columnWidths.shift,
    columnWidths.schedule,
    columnWidths.count,
    columnWidths.date,
].join(" ");
const tableHeaderHeight = designSystemStyles.sizeRowMedium;
// Trimmed centrally: some of these values (building name, etc.) get wrapped in
// **bold** markdown elsewhere, and a leading/trailing space breaks CommonMark's
// bold parsing (leaves the literal ** on screen instead of rendering bold).
const renderText = (value: string | undefined) => (value ?? "").trim();

interface ModalDetailSection {
    title: string;
    fields: Array<{
        label: string;
        value?: string | number | null;
    }>;
}

const buildDetailFields = (row: AlertasListItem | null, detail: AlertDetail | null) => {
    const alert = detail?.alert;
    const alertType = alert?.alertType ?? row?.row.alertType;
    if (isKioskEvidenceSpoofAlert(alertType)) {
        const recordID = alert?.metadata?.recordID;
        return {
            isKioskEvidenceSpoof: true,
            recordID: typeof recordID === "string" ? recordID : undefined,
            buildingName: detail?.buildingName ?? row?.buildingName,
            shiftLabel: kioskEvidenceSpoofShiftLabel(),
            scheduleLabel: "—",
            countLabel: "—",
            missingCount: "—",
            dateLabel: row?.dateLabel ?? alert?.day,
            description: alert?.largeDescription ?? alert?.shortDescription ?? row?.description,
            type: typeToLabel(alertType),
            importance: alert?.alertImportance ?? row?.importanceLabel,
        };
    }
    const shiftInstance = detail?.shiftInstance ?? row?.row.shiftInstance;
    const scheduleLabel = shiftInstance?.hourFrom && shiftInstance?.hourTo
        ? `${shiftInstance.hourFrom} - ${shiftInstance.hourTo}`
        : row?.scheduleLabel;
    const expectedCount = detail?.expectedCount ?? row?.row.expectedCount;
    const checkinCount = detail?.checkinCount ?? row?.row.checkinCount;
    const missingCount = detail?.missingCount ?? row?.row.missingCount;

    return {
        isKioskEvidenceSpoof: false,
        recordID: undefined,
        buildingName: detail?.buildingName ?? row?.buildingName,
        shiftLabel: detail?.shiftRecipe?.name ?? row?.shiftLabel,
        scheduleLabel,
        countLabel: checkinCount != null && expectedCount != null ? `${checkinCount} / ${expectedCount}` : row?.countLabel,
        missingCount,
        dateLabel: row?.dateLabel ?? alert?.day,
        description: alert?.shortDescription ?? alert?.largeDescription ?? row?.description,
        type: alert?.alertType ?? row?.typeLabel,
        importance: alert?.alertImportance ?? row?.importanceLabel,
    };
};

const DetailSection: React.FC<{ section: ModalDetailSection }> = ({ section }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${section.title.trim()}**`} color={designSystemStyles.colorText} markdown={true} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                {section.fields.map((field) => (
                    <DetailField
                        key={`${section.title}-${field.label}`}
                        label={field.label}
                        value={field.value}
                    />
                ))}
            </ColumnsContainer>
        </VerticalContainer>
    </VerticalContainer>
);

const userGridTemplateColumns = "minmax(0, 34fr) minmax(0, 18fr) minmax(0, 30fr) minmax(0, 18fr)";

const trackAlertDetailShown = (row: AlertasListItem) => {
    AnalyticsHelper.trackAlertDetailShown({
        alertID: row.row.id ?? row.id,
        day: row.row.day,
        buildingID: row.row.buildingID,
        buildingName: row.buildingName,
        shiftInstanceID: row.row.shiftInstanceID,
        recipeID: row.row.recipeID,
        alertType: row.row.alertType != null ? String(row.row.alertType) : undefined,
        alertImportance: row.row.alertImportance != null ? String(row.row.alertImportance) : undefined,
    });
};

const UserListSection: React.FC<{
    title: string;
    users: AlertDetail["missingUsers"] | undefined;
}> = ({ title, users }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${title.trim()}**`} color={designSystemStyles.colorText} markdown={true} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            {!users || users.length === 0 ? (
                <Text text={"N/D".localized()} color={designSystemStyles.colorTextSecondary} />
            ) : (
                <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                    <TableHeaderContainer gridTemplateColumns={userGridTemplateColumns}>
                        <TableHeaderCell text={"Usuario".localized()} width="100%" />
                        <TableHeaderCell text={"Legajo".localized()} width="100%" />
                        <TableHeaderCell text={"Teléfono".localized()} width="100%" />
                    </TableHeaderContainer>
                    {users.map((user) => (
                        <TableRowContainer key={user.id} gridTemplateColumns={userGridTemplateColumns}>
                            <TableCell text={renderText(user.name ?? user.id)} width="100%" truncate={true} />
                            <TableCell text={renderText(user.legajo)} width="100%" truncate={true} />
                            <TableCell text={renderText(user.mobilePhone)} width="100%" truncate={true} />
                        </TableRowContainer>
                    ))}
                </VerticalContainer>
            )}
        </VerticalContainer>
    </VerticalContainer>
);

const AlertDetailModal = ({
    row,
    detail,
    loading,
    error,
    isOpen,
    onClose,
}: {
    row: AlertasListItem | null;
    detail: AlertDetail | null;
    loading: boolean;
    error: string | null;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const fields = buildDetailFields(row, detail);
    const sections: ModalDetailSection[] = [
        {
            title: "Resumen".localized(),
            fields: [
                { label: "Edificio".localized(), value: fields.buildingName },
                { label: "Turno".localized(), value: fields.shiftLabel },
                { label: "Horario".localized(), value: fields.scheduleLabel },
                { label: "Conteo".localized(), value: fields.countLabel },
                { label: "Faltantes".localized(), value: fields.missingCount },
                { label: "Fecha".localized(), value: fields.dateLabel },
            ],
        },
        {
            title: "Alerta".localized(),
            fields: [
                { label: "Tipo".localized(), value: fields.type },
                { label: "Importancia".localized(), value: fields.importance },
                { label: "Descripción".localized(), value: fields.description },
            ],
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Detalle de alerta".localized()}
            width="1020px"
            height="75vh"
            maxWidth="92vw"
            maxHeight="86vh"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {loading ? (
                        <QontyLoading text={"Cargando detalle...".localized()} withQonty={true} />
                    ) : error ? (
                        <QontyEmptyState text={error} darkMode={false} />
                    ) : (
                        <VerticalContainer width="100%" gap={designSystemStyles.gapLg} paddingBottom={designSystemStyles.paddingSm}>
                            {sections.map((section) => (
                                <DetailSection key={section.title} section={section} />
                            ))}
                            {fields.isKioskEvidenceSpoof && fields.recordID && (
                                <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
                                    <Text text={`**${"Fotos de la fichada".localized()}**`} color={designSystemStyles.colorText} markdown={true} />
                                    <KioskEvidencePhotos recordID={fields.recordID} />
                                </VerticalContainer>
                            )}
                            <UserListSection
                                title={fields.isKioskEvidenceSpoof ? "Colaborador".localized() : "Usuarios presentes".localized()}
                                users={detail?.checkedInUsers}
                            />
                            {!fields.isKioskEvidenceSpoof && (
                                <UserListSection
                                    title={"Usuarios faltantes".localized()}
                                    users={detail?.missingUsers}
                                />
                            )}
                        </VerticalContainer>
                    )}
                </div>
                <HorizontalContainer justifyContent="flex-end" width="100%">
                    <HollowedButton
                        text={"Cerrar".localized()}
                        onClick={onClose}
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

const AlertasTableRow = ({ row, onOpenDetail }: { row: AlertasListItem; onOpenDetail: (row: AlertasListItem) => void }) => {
    return (
        <TableRowContainer
            gridTemplateColumns={tableGridTemplateColumns}
            gap={designSystemStyles.gapMd}
        >
            <ClickableWrapper
                onClick={() => onOpenDetail(row)}
                style={{ width: "100%", minWidth: 0 }}
                title={"Ver detalle de alerta".localized()}
            >
                <TableCell
                    text={`**${renderText(row.buildingName)}**`}
                    width="100%"
                    truncate={true}
                />
            </ClickableWrapper>
            <TableCell
                text={renderText(row.shiftLabel)}
                width="100%"
                truncate={true}
            />
            <TableCell
                text={renderText(row.scheduleLabel)}
                width="100%"
                truncate={true}
            />
            <TableCell
                text={renderText(row.countLabel)}
                width="100%"
                truncate={true}
            />
            <TableCell
                text={renderText(row.dateLabel)}
                width="100%"
                justifyContent="right"
                truncate={true}
            />
        </TableRowContainer>
    );
};

const AlertasList = ({ orgID, timezone }: AlertasListProps) => {
    const service = useMemo(() => {
        return new AlertsService(Application.shared().coreDependencies.httpClient);
    }, []);
    const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
    const hasTrackedShownRef = useRef(false);

    const viewModel = useAlertasListViewModel({
        orgID,
        timezone,
        service,
    });

    useEffect(() => {
        if (hasTrackedShownRef.current) {
            return;
        }

        hasTrackedShownRef.current = true;
        AnalyticsHelper.trackAlertsShown(viewModel.day);
    }, [viewModel.day]);

    const handleRefresh = () => {
        AnalyticsHelper.trackAlertsRefreshed(viewModel.day);
        void viewModel.refresh();
    };

    const handleOpenDetail = (row: AlertasListItem) => {
        trackAlertDetailShown(row);
        void viewModel.openAlertDetail(row);
    };

    const handleDateSelected = (date: Date) => {
        const nextDay = viewModel.getDayFromDate(date);
        AnalyticsHelper.trackAlertsDateChanged(nextDay);
        viewModel.setSelectedDate(date);
        setIsDateSelectorOpen(false);
    };

    const emptyStateText = viewModel.searchField.length > 0
        ? "No hay resultados para tu búsqueda.".localized()
        : "No hay alertas para mostrar en la fecha seleccionada.".localized();

    const centeredTableState = (content: ReactNode) => (
        <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
            {content}
        </VerticalContainer>
    );

    const tableContent = viewModel.loading ? (
        centeredTableState(
            <QontyLoading
                text={"Cargando alertas...".localized()}
                withQonty={true}
            />
        )
    ) : viewModel.error ? (
        centeredTableState(
            <QontyEmptyState
                text={viewModel.error}
                darkMode={false}
            />
        )
    ) : viewModel.rows.length === 0 ? (
        centeredTableState(
            <QontyEmptyState
                text={emptyStateText}
                darkMode={false}
            />
        )
    ) : (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: designSystemStyles.gapMd, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
            <TableHeaderContainer
                height={tableHeaderHeight}
                gridTemplateColumns={tableGridTemplateColumns}
                gap={designSystemStyles.gapMd}
            >
                <TableHeaderCell text={"Edificio".localized()} width="100%" />
                <TableHeaderCell text={"Turno".localized()} width="100%" />
                <TableHeaderCell text={"Horario".localized()} width="100%" />
                <TableHeaderCell text={"Conteo".localized()} width="100%" />
                <TableHeaderCell text={"Fecha".localized()} width="100%" justifyContent="right" />
            </TableHeaderContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0, width: "100%", maxWidth: "100%" }}>
                {viewModel.rows.map((row) => (
                    <AlertasTableRow key={row.id} row={row} onOpenDetail={handleOpenDetail} />
                ))}
            </div>
        </div>
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                gap: designSystemStyles.gapLg,
            }}
        >
            <HorizontalContainer
                height={designSystemStyles.sizeRowMedium}
                width="100%"
                justifyContent="space-between"
                gap={designSystemStyles.gapMd}
                overflow="hidden"
            >
                <HorizontalContainer width="auto" maxWidth="calc(100% - 210px)" gap={designSystemStyles.gapMd} overflow="hidden">
                    <SearchBar
                        placeholder={"Buscar alerta...".localized()}
                        value={viewModel.searchField}
                        onChange={viewModel.setSearchField}
                        width="360px"
                    />
                    <RefreshButton
                        text=""
                        onClick={handleRefresh}
                    />
                </HorizontalContainer>
                <DateTriggerButton
                    text={viewModel.formattedDay}
                    onClick={() => setIsDateSelectorOpen(true)}
                    width="190px"
                />
            </HorizontalContainer>

            <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", maxWidth: "100%", minHeight: 0, overflow: "hidden" }}>
                {tableContent}
            </div>

            <Modal
                isOpen={isDateSelectorOpen}
                onClose={() => setIsDateSelectorOpen(false)}
                title={"Seleccionar fecha".localized()}
                width="420px"
                maxWidth="92vw"
            >
                <LocalDateSelector
                    initialDate={viewModel.selectedDate}
                    events={{
                        onApply: (date) => {
                            handleDateSelected(date ?? new Date());
                        },
                        onClear: () => {
                            handleDateSelected(new Date());
                        },
                    }}
                />
            </Modal>
            <AlertDetailModal
                row={viewModel.selectedRow}
                detail={viewModel.selectedDetail}
                loading={viewModel.detailLoading}
                error={viewModel.detailError}
                isOpen={viewModel.selectedRow != null}
                onClose={viewModel.closeAlertDetail}
            />
        </div>
    );
};

export default AlertasList;
